import { useState } from 'react';
import { pressArticles, PressArticle } from '@/mocks/press';

const typeLabel: Record<string, string> = {
  interview: 'Interview',
  feature: 'Feature',
  review: 'Review',
  mention: 'Mention',
};

const typeBadgeColor: Record<string, string> = {
  interview: 'bg-amber-100 text-amber-800',
  feature: 'bg-stone-100 text-stone-700',
  review: 'bg-emerald-100 text-emerald-800',
  mention: 'bg-rose-100 text-rose-700',
};

// Group articles by image orientation so similar-sized images sit together
// Portrait group: ids 3, 4, 7, 10 (tall images)
// Landscape/square group: ids 1, 2, 5, 6, 8, 9, 11, 12
const PORTRAIT_IDS = new Set([3, 4, 7, 10]);

const portraitArticles = pressArticles.filter((a) => PORTRAIT_IDS.has(a.id));
const landscapeArticles = pressArticles.filter((a) => !PORTRAIT_IDS.has(a.id));

// Reordered: landscape group first (8 cards), then portrait group (4 cards)
const orderedArticles: PressArticle[] = [...landscapeArticles, ...portraitArticles];

function PressCard({
  article,
  imgHeight,
}: {
  article: PressArticle;
  imgHeight: string;
}) {
  const [imgSrc, setImgSrc] = useState(article.coverImage ?? article.coverFallback ?? '');

  const handleError = () => {
    if (article.coverFallback && imgSrc !== article.coverFallback) {
      setImgSrc(article.coverFallback);
    }
  };

  return (
    <article
      itemScope
      itemType="https://schema.org/NewsArticle"
      className="bg-white rounded-lg overflow-hidden flex flex-col border border-stone-100"
    >
      <meta itemProp="headline" content={article.title} />
      <meta itemProp="datePublished" content={article.year} />
      <link itemProp="publisher" href={article.url} />
      <meta itemProp="author" content={article.outlet} />
      <meta itemProp="about" content="Oludamola Adebowale" />

      {/* Cover image — clickable link to article */}
      {imgSrc && (
        <a
          href={article.url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className={`w-full flex-shrink-0 bg-stone-100 overflow-hidden block cursor-pointer ${imgHeight}`}
          aria-label={`Read article: ${article.title}`}
        >
          <img
            src={imgSrc}
            alt={article.title}
            onError={handleError}
            className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
            itemProp="image"
          />
        </a>
      )}

      <div className="p-5 flex flex-col flex-1">
        {/* Outlet + type row */}
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <span className="font-sans text-xs font-bold uppercase tracking-wider text-gray-800 whitespace-nowrap">
            {article.outlet}
          </span>
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
              typeBadgeColor[article.type] ?? 'bg-gray-100 text-gray-600'
            }`}
          >
            {typeLabel[article.type]}
          </span>
        </div>

        <p className="text-xs text-gray-400 font-sans mb-2">{article.year}</p>
        <h3 className="font-serif text-base font-semibold text-gray-900 mb-3 leading-snug flex-1">
          {article.title}
        </h3>
        <p className="font-sans text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
          {article.excerpt}
        </p>
        <a
          href={article.url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="inline-flex items-center gap-1 font-sans text-xs uppercase tracking-wider text-[#b89a6a] hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
        >
          Read Article <i className="ri-external-link-line" />
        </a>
      </div>
    </article>
  );
}

export default function PressSection() {
  return (
    <section id="press" className="bg-white py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="w-2 h-2 rounded-full bg-accent mr-3"></div>
            <p className="text-xs uppercase tracking-widest text-gray-500">RECOGNITION</p>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Media Press Mentions
          </h2>
          <p className="font-sans text-base text-gray-600 max-w-2xl mx-auto">
            Featured across leading Nigerian and international media, from CNN and BBC to The Guardian, ThisDay, and beyond.
          </p>
        </div>

        {/* Landscape group — 8 cards with landscape image height */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {landscapeArticles.map((article) => (
            <PressCard key={article.id} article={article} imgHeight="h-52" />
          ))}
        </div>

        {/* Portrait group — 4 cards with taller image height */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {portraitArticles.map((article) => (
            <PressCard key={article.id} article={article} imgHeight="h-80" />
          ))}
        </div>
      </div>
    </section>
  );
}
